import { useLingui } from '@lingui/react/macro';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { useTranslation } from '@/lib/i18n';
import { buildCampaignHref } from '../portal-href';
import type { PartnerNgoCampaign } from './use-partner-ngos';

/** One "1700 / Beneficiarios directos" pair under the cover. */
function Reach(props: { value: number; label: string }) {
	const { language } = useTranslation();
	return (
		<div className="flex flex-col">
			<span className="text-2xl leading-[1.5] font-medium text-black tabular-nums">
				{formatNumber(language, props.value, 0)}
			</span>
			<span className="text-xs leading-normal text-[#7a7a7a]">{props.label}</span>
		</div>
	);
}

/**
 * A campaign as the partner-NGO modal draws it (`216:6395`): a 676x313 card split down the middle —
 * cover and reach figures on the left, funding progress over the title and summary on the right.
 * Its cover, name and description come from the project behind the campaign.
 *
 * A different card from the homepage carousel's, not a variant of it: that one is a narrow portrait
 * tile with the NGO lockup, this one is a wide landscape panel carrying beneficiary counts and a
 * description the carousel never shows. Keeping them apart is what lets each match its own hand-off.
 *
 * The whole card is a link to the campaign's page in its merchant's portal — a different origin
 * (`correos.worldcoo.com`), so a plain `<a>` rather than a router `<Link>`, and the same destination
 * the homepage carousel uses. Every campaign that reaches this component has a portal: the backend
 * drops the ones whose merchant has none, precisely so this card never has to decide whether it is
 * clickable. A sheet mixing cards that open with cards that do nothing teaches the visitor that none
 * of them are clickable.
 *
 * It opens in a NEW TAB, as the merchant logo wheel's links do: this is the marketing site, and a
 * campaign page is a different host, so following a card in place throws the reader out of the site
 * they were browsing — and out of this sheet, which they would have to reopen on the partner they
 * had found. `rel="noopener noreferrer"` alongside, matching every other external link in this route
 * group, and an `sr-only` note because a link that retargets the window should say so.
 */
export function NgoCampaignCard(props: { campaign: PartnerNgoCampaign; baseUrl: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const campaign = props.campaign;
	const raised = Math.round(campaign.collectedCents / 100);
	const goal = campaign.economicGoal;
	const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : undefined;
	const hasReach = campaign.beneficiaries.direct > 0 || campaign.beneficiaries.indirect > 0;

	return (
		<a
			href={buildCampaignHref(props.baseUrl, campaign.portalId, campaign.slug)}
			target="_blank"
			rel="noopener noreferrer"
			className="flex flex-col gap-4 rounded-3xl bg-white p-4 shadow-[0_9px_24px_0_rgba(46,20,0,0.12)] transition-shadow hover:shadow-[0_12px_32px_0_rgba(46,20,0,0.2)] sm:flex-row"
		>
			<div className="flex shrink-0 flex-col gap-4 sm:w-[292px]">
				<div className="relative h-[197px] overflow-hidden rounded-lg">
					<Image
						src={campaign.coverImage}
						alt={campaign.name}
						height={394}
						aspectRatio={292 / 197}
						objectFit="cover"
						className="absolute inset-0 size-full object-cover!"
					/>
					{/* The hand-off darkens the top-left corner so a pale photograph still separates from the
					    white card behind it. */}
					<div aria-hidden className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
				</div>

				{/* Hidden when the project behind the campaign records no reach at all. Work that helps
				    nobody does not exist — a pair of zeroes means the NGO never filled the field in, and
				    printing "0 beneficiarios" states something false rather than admitting we do not know. */}
				{hasReach && (
					<div className="flex gap-10">
						<Reach value={campaign.beneficiaries.direct} label={t`Direct beneficiaries`} />
						<Reach value={campaign.beneficiaries.indirect} label={t`Indirect beneficiaries`} />
					</div>
				)}
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<div className="flex flex-col gap-[5px]">
					{/* Whole euros, as the hand-off prints them ("1.860 € de 15.000 €"). */}
					<p className="text-xs leading-[1.3] font-medium tracking-[-0.02em]">
						<span className="text-ink">{formatNumber(language, raised, 0)} €</span>
						{goal ? (
							<span className="text-ink/50">
								{' '}
								{t`of`} {formatNumber(language, goal, 0)} €
							</span>
						) : null}
					</p>
					{pct !== undefined && (
						<div className="bg-sand h-[3px] overflow-hidden rounded">
							<div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} />
						</div>
					)}
				</div>

				<h3 className="text-ink text-xl leading-[1.4] font-medium tracking-[-0.02em]">{campaign.name}</h3>

				{/* Six lines is the height the hand-off gives this column; the server already caps the text,
				    so the clamp is what keeps a short cap and a long one the same card height. */}
				<p className="line-clamp-6 text-sm leading-[22px] text-black">{campaign.description}</p>
				<span className="sr-only">{t`(opens in a new tab)`}</span>
			</div>
		</a>
	);
}
